import React, { useState } from 'react';
import { Card, Button, Input, Space, List, Tag, Spin, App, Alert } from 'antd';
import { RobotOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { OiPageFrame, OiEmptyState } from '@/components/owner-intelligence/OwnerIntelligenceComponents';

const PROMPTS = [
  'What should I worry about today?',
  'Where is my money stuck?',
  'Why did margin drop?',
  'Which customer is risky?',
  'What changed since last week?',
];

const ExecutiveAdvisorPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<{
    answer: string;
    evidence: Array<{ label: string; value: string | number; link?: string }>;
    confidence: number | null;
    impact: string;
    recommendations: Array<{ title: string; description: string; linked_route?: string }>;
    source?: 'llm' | 'guided';
  } | null>(null);

  const ask = async (q: string) => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const data = await ownerIntelligenceService.askAdvisor(q);
      setResponse(data as typeof response);
      setQuestion(q);
    } catch (e: unknown) {
      message.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OiPageFrame subtitle="Executive advisor — grounded answers from live ERP data when AI is enabled; guided insights otherwise.">
      {response?.source === 'guided' ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Guided insights mode"
          description="AI is unavailable or returned no result. Answers use rule-based templates from command center data."
        />
      ) : null}

      <Card title="Ask a question" style={{ marginBottom: 16 }} bordered={false}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What should I worry about today?"
            onPressEnter={() => ask(question)}
          />
          <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={() => ask(question)}>
            Ask
          </Button>
        </Space.Compact>
        <div style={{ marginTop: 12 }}>
          {PROMPTS.map((p) => (
            <Button key={p} size="small" style={{ margin: '4px 4px 0 0' }} icon={<RobotOutlined />} onClick={() => ask(p)}>
              {p}
            </Button>
          ))}
        </div>
      </Card>

      {!response && !loading && (
        <OiEmptyState
          title="Ask your first question"
          body="Use a suggested prompt or type a question to get evidence-backed answers with ERP sources."
          actionLabel="What should I worry about today?"
          onAction={() => ask(PROMPTS[0])}
        />
      )}

      {loading && <Spin style={{ display: 'block', margin: '24px auto' }} />}

      {response && !loading && (
        <Card title="Answer" bordered={false}>
          <p>{response.answer}</p>
          <Space style={{ marginBottom: 16 }}>
            {response.source === 'llm' ? <Tag color="blue">AI · ERP sources</Tag> : <Tag>Guided insights</Tag>}
            <Tag>{response.impact}</Tag>
          </Space>
          {response.evidence.length > 0 && (
            <>
              <h4 style={{ color: 'var(--oi-text-secondary)' }}>Sources</h4>
              <List
                size="small"
                dataSource={response.evidence}
                renderItem={(e) => (
                  <List.Item>
                    <strong>{e.label}</strong>: {e.value}
                    {e.link && (
                      <Button type="link" size="small" onClick={() => navigate(e.link!)}>
                        View
                      </Button>
                    )}
                  </List.Item>
                )}
              />
            </>
          )}
          {response.recommendations.length > 0 && (
            <>
              <h4 style={{ color: 'var(--oi-text-secondary)' }}>Recommended actions</h4>
              <List
                dataSource={response.recommendations}
                renderItem={(r) => (
                  <List.Item
                    actions={r.linked_route ? [<Button key="go" type="link" onClick={() => navigate(r.linked_route!)}>Go</Button>] : []}
                  >
                    <List.Item.Meta title={r.title} description={r.description} />
                  </List.Item>
                )}
              />
            </>
          )}
        </Card>
      )}
    </OiPageFrame>
  );
};

export default ExecutiveAdvisorPage;
